/**
 * Performance instrumentation for magic link authentication flow.
 * Uses the Performance API to measure timing of auth flow stages.
 */

export type AuthSystem = 'gcauth' | 'cognito';

interface AuthContext {
  authSystem: AuthSystem;
  retryCount: number;
}

let authContext: AuthContext = {
  authSystem: 'gcauth',
  retryCount: 0
};

export const AUTH_PERF_MARKS = {
  MAGIC_LINK_START: 'gc:auth:start',
  SIGN_IN_START: 'gc:auth:signin:start',
  SIGN_IN_END: 'gc:auth:signin:end',
  CHECK_LINK_START: 'gc:auth:check:start',
  CHECK_LINK_END: 'gc:auth:check:end',
  FETCH_USER_START: 'gc:auth:fetch-user:start',
  FETCH_USER_END: 'gc:auth:fetch-user:end',
  AUTH_END: 'gc:auth:end',
  TECH_CHECK_READY: 'gc:auth:tech-check-ready'
} as const;

export interface AuthTimingData {
  signInDurationMs: number | null;
  checkLinkDurationMs: number | null;
  fetchUserDurationMs: number | null;
  totalAuthDurationMs: number | null;
  timeToTechCheckMs: number | null;
  retryCount: number;
  authSystem: AuthSystem;
}

export function setAuthContext(system: AuthSystem, retries: number = 0): void {
  authContext = {
    authSystem: system,
    retryCount: retries
  };
}

export function incrementRetryCount(): void {
  authContext.retryCount += 1;
}

export function getStoredAuthContext(): AuthContext {
  return { ...authContext };
}

function safeMark(name: string): void {
  try {
    performance.mark(name);
  } catch {
    // Performance API not available or mark failed
  }
}

function getMeasureDuration(startMark: string, endMark: string): number | null {
  try {
    const startEntries = performance.getEntriesByName(startMark);
    const endEntries = performance.getEntriesByName(endMark);

    if (startEntries.length === 0 || endEntries.length === 0) {
      return null;
    }

    const start = startEntries[0];
    const end = endEntries[0];
    return end.startTime - start.startTime;
  } catch {
    return null;
  }
}

function getTimeSinceMark(markName: string): number | null {
  try {
    const entries = performance.getEntriesByName(markName);
    if (entries.length === 0) {
      return null;
    }
    return performance.now() - entries[0].startTime;
  } catch {
    return null;
  }
}

function getMarkTimestamp(markName: string): number | null {
  try {
    const entries = performance.getEntriesByName(markName);
    if (entries.length === 0) {
      return null;
    }
    return entries[0].startTime;
  } catch {
    return null;
  }
}

export function markAuthStart(): void {
  safeMark(AUTH_PERF_MARKS.MAGIC_LINK_START);
}

export function markSignInStart(): void {
  safeMark(AUTH_PERF_MARKS.SIGN_IN_START);
}

export function markSignInEnd(): void {
  safeMark(AUTH_PERF_MARKS.SIGN_IN_END);
}

export function markCheckLinkStart(): void {
  safeMark(AUTH_PERF_MARKS.CHECK_LINK_START);
}

export function markCheckLinkEnd(): void {
  safeMark(AUTH_PERF_MARKS.CHECK_LINK_END);
}

export function markFetchUserStart(): void {
  safeMark(AUTH_PERF_MARKS.FETCH_USER_START);
}

export function markFetchUserEnd(): void {
  safeMark(AUTH_PERF_MARKS.FETCH_USER_END);
}

export function markAuthEnd(): void {
  safeMark(AUTH_PERF_MARKS.AUTH_END);
}

export function markTechCheckReady(): void {
  safeMark(AUTH_PERF_MARKS.TECH_CHECK_READY);
}

export function getAuthTimingData(): AuthTimingData {
  const signInDurationMs = getMeasureDuration(AUTH_PERF_MARKS.SIGN_IN_START, AUTH_PERF_MARKS.SIGN_IN_END);

  const checkLinkDurationMs = getMeasureDuration(AUTH_PERF_MARKS.CHECK_LINK_START, AUTH_PERF_MARKS.CHECK_LINK_END);

  const fetchUserDurationMs = getMeasureDuration(AUTH_PERF_MARKS.FETCH_USER_START, AUTH_PERF_MARKS.FETCH_USER_END);

  const totalAuthDurationMs = getMeasureDuration(AUTH_PERF_MARKS.MAGIC_LINK_START, AUTH_PERF_MARKS.AUTH_END);

  const completeTimestamp = getMarkTimestamp(AUTH_PERF_MARKS.TECH_CHECK_READY);
  const startTimestamp = getMarkTimestamp(AUTH_PERF_MARKS.MAGIC_LINK_START);
  const timeToTechCheckMs =
    completeTimestamp !== null && startTimestamp !== null ? completeTimestamp - startTimestamp : null;

  return {
    signInDurationMs,
    checkLinkDurationMs,
    fetchUserDurationMs,
    totalAuthDurationMs,
    timeToTechCheckMs,
    retryCount: authContext.retryCount,
    authSystem: authContext.authSystem
  };
}

export function clearAuthMarks(): void {
  try {
    Object.values(AUTH_PERF_MARKS).forEach(markName => {
      performance.clearMarks(markName);
    });
  } catch {
    // Performance API not available
  }

  authContext = {
    authSystem: 'gcauth',
    retryCount: 0
  };
}

export function hasAuthStartMark(): boolean {
  try {
    return performance.getEntriesByName(AUTH_PERF_MARKS.MAGIC_LINK_START).length > 0;
  } catch {
    return false;
  }
}
